import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form, useNavigate } from 'react-router-dom';
import {
  InputWrapper,
  LoginFormButton,
  LoginFormFooter,
  LoginFormHeader,
  LoginAgainFormButton
} from '../atoms';
import { z } from 'zod';
import { useContext, useState } from 'react';
import StatusCodes from 'http-status-codes';
import { APIEndPoints, Request } from '../../apis';
import type { UserType } from '../../contexts/UserContext';
import { getUserInfo, UserContext } from '../../contexts/UserContext';
import { withNoAuthRedirect } from '../hocs/withNoAuthRedirect';
import jwt_decode from 'jwt-decode';
import { useMutation } from '@tanstack/react-query';
import { type SignInTO } from '../../common/types/Authentication';
import { allPaths } from '../../routes/Paths';

const codeFormSchema = z.object({
  code: z.string().length(6, 'Please enter your 6 digit code.')
});

type CodeFormScheme = z.infer<typeof codeFormSchema>;
const getSub = (decodedToken: unknown): string => {
  if (
    typeof decodedToken === 'object' &&
    decodedToken !== null &&
    'sub' in decodedToken
  ) {
    if (typeof decodedToken.sub === 'string') {
      return decodedToken.sub;
    }
  }
  return 'no-sub-found';
};

const SMSVerificationPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<CodeFormScheme>({
    resolver: zodResolver(codeFormSchema)
  });

  const [showLoginButton, setShowLoginButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { loginData, onUserChange, setMerchantServiceUserInfo, logOut } =
    useContext(UserContext);

  const {
    isLoading: verifyingCode,
    mutate: verifyCode,
    error: submitError
  } = useMutation({
    mutationFn: (data: CodeFormScheme) => {
      const { email, session, challengeName } = loginData;
      // TODO log
      // console.log('email', email);
      // console.log('session', session);
      // console.log('challengeName', challengeName);
      return Request.post(
        `${APIEndPoints.signInChallenge}/${encodeURIComponent(email)}`,
        // SignInTOSchema,
        z.any(),
        {
          answer: data.code,
          session,
          challengeName: challengeName
        }
      );
    },
    onSuccess: async (data: SignInTO) => {
      const { signInResult, userRole, merchant } = data;

      if (signInResult) {
        localStorage.setItem('lastActivity', Date.now().toString());
        const { AccessToken } = signInResult;

        if (AccessToken && merchant) {
          const sub = getSub(jwt_decode(AccessToken));

          onUserChange({
            merchantId: merchant,
            userType: userRole as UserType,
            sub: sub,
            email: loginData.email
          });

          const userInfo = await getUserInfo(sub);
          // TODO log
          //console.log('userInfo', userInfo);
          if (userInfo) {
            // TODO log
            //console.log('___userInfo: setMerchantServiceUserInfo');
            setMerchantServiceUserInfo(userInfo);
          } else {
            // TODO: remove this after testing / before MVP
            // TODO log
            // console.log(
            //   '___userInfo ERRROR... most likely user not found in local db'
            // );
          }
        } else {
          setErrorMessage('Something went wrong');
        }
      }
    },
    onError: (err: any) => {
      console.log('err', JSON.stringify(err, null, 2));
      if (err.status === StatusCodes.UNAUTHORIZED) {
        setShowLoginButton(false);
      }
    }
  });

  const onSubmit = (formData: CodeFormScheme) => {
    verifyCode(formData);
  };

  return (
    <div>
      <LoginFormHeader>
        <h1>SMS Verification</h1>
        <p>
          We’ve sent a unique 6 digit code to the mobile number you have
          registered.
        </p>
      </LoginFormHeader>

      <Form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
        <InputWrapper
          name="code"
          type="text"
          label="Please enter your verification code"
          required
          register={register}
          errorMessage={
            submitError?.message || errors.code?.message || errorMessage
          }
          autoFill={false}
        />
        <LoginFormFooter
          horizontalSpacing="10px"
          verticalSpacing="0px"
          justify="center"
        >
          <LoginFormButton
            disabled={verifyingCode}
            isLoading={verifyingCode}
            type="submit"
          >
            Verify Me
          </LoginFormButton>

          {showLoginButton ||
            (submitError?.message && (
              <LoginAgainFormButton
                onClick={async () => {
                  await logOut();
                }}
                type="button"
              >
                Login Again
              </LoginAgainFormButton>
            ))}
        </LoginFormFooter>
      </Form>
    </div>
  );
};

const SecureSMSVerificationPage = withNoAuthRedirect(SMSVerificationPage);
export { SecureSMSVerificationPage as SMSVerificationPage };
